'use client';

import { Trash2 } from 'lucide-react';
import SafeImage from '@ui/SafeImage';
import Toggle from '@ui/Toggle';
import { removeUserBike } from '@util/firestore/users';
import { prefix } from '@util/imagePrefix';
import { cn } from '@util/index';
import { getMakeLogo } from 'app/create-listing/_components/BikeFitment';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import type { Bike } from 'models/shared';
import { useState } from 'react';

type EditGarageFormProps = {
  bikes: Bike[];
  onAddBikeClick: () => void;
  onSyncSelectedBikes: (bikes: Bike[]) => void;
  previousSelectedBikes: Bike[];
};

export default function EditGarageForm({
  bikes,
  onAddBikeClick,
  onSyncSelectedBikes,
  previousSelectedBikes,
}: EditGarageFormProps) {
  const [selectedBikes, setSelectedBikes] = useState<Bike[]>(
    previousSelectedBikes
  );

  const { userDoc } = useAuth();
  const { showSuccessToast } = useToastContext();

  const deleteBike = async (item: Bike) => {
    if (!userDoc) return;
    if (window.confirm('Are you sure you want to delete this bike?')) {
      await removeUserBike(userDoc?.uid, item);
      showSuccessToast('Bike deleted successfully');
    }
  };

  return (
    <div className="relative flex h-full w-full flex-col">
      <div className="relative max-h-[55rem] flex-1 overflow-y-auto px-8 pb-[13rem] pt-8">
        {bikes.length ? (
          <>
            {' '}
            {bikes
              .sort((a, b) => {
                // Sort by make alphabetically
                return a.make.localeCompare(b.make);
              })
              .map((bike, index) => (
                <div
                  className={cn(
                    'mb-8 flex h-auto w-full items-center justify-between pb-4',
                    index !== bikes.length - 1 && 'border-b border-zinc-100'
                  )}
                  key={`garage-bike-${bike.make}-${bike.model}-${bike.year}`}
                >
                  <div className="flex items-center gap-x-8 pb-4">
                    <div className="flex w-[10rem] items-center justify-center">
                      <SafeImage
                        src={getMakeLogo(prefix, bike.make)}
                        alt={bike.make}
                        className="mt-4 h-[6rem] w-auto object-contain object-center"
                        fallback={
                          <div className="flex h-[6rem] items-center py-6 text-[1.6rem] font-bold uppercase">
                            {bike.make}
                          </div>
                        }
                      />
                    </div>

                    <div className="flex flex-col gap-y-2">
                      <span className="text-[1.8rem] font-semibold text-black">
                        {bike.make}
                      </span>

                      <div className="flex items-center gap-x-1">
                        <span className="truncate text-[1.6rem] font-medium text-[#7F7F7F]">
                          {bike.year}
                        </span>

                        <span className="w-[18rem] truncate text-[1.6rem] font-medium text-[#7F7F7F]">
                          {bike.model}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <Toggle
                      enabled={selectedBikes.includes(bike)}
                      onChange={() => {
                        if (selectedBikes.includes(bike)) {
                          setSelectedBikes(
                            selectedBikes.filter((b) => b !== bike)
                          );
                        } else {
                          setSelectedBikes([...selectedBikes, bike]);
                        }
                      }}
                    />

                    <button type="button" onClick={() => deleteBike(bike)}>
                      <Trash2 className="h-[3rem] w-[3rem] text-brand-secondary transition-colors duration-75 ease-linear hover:text-brand-secondary-darker" />
                    </button>
                  </div>
                </div>
              ))}
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center py-16">
            <div className="flex flex-col items-center gap-1">
              <h3 className="text-[1.8rem] font-semibold text-zinc-800">
                You have no vehicles in your garage
              </h3>
              <p className="text-[1.6rem] font-medium text-zinc-500">
                Add your first vehicle to get started
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="absolute bottom-0 left-0 flex w-full justify-between gap-8 border-t border-zinc-200 bg-white p-8">
        <button
          type="button"
          className="flex h-[5rem] w-full items-center justify-center rounded-[1.5rem] border border-zinc-700 bg-transparent text-[1.8rem] font-medium text-zinc-900 transition-colors duration-150 ease-out hover:border-black hover:text-black"
          onClick={onAddBikeClick}
        >
          Add Vehicle
        </button>

        <button
          type="button"
          className="flex h-[5rem] w-full items-center justify-center rounded-[1.5rem] bg-brand-secondary text-[1.8rem] font-medium text-white transition-colors duration-150 ease-out hover:bg-brand-secondary-darker"
          onClick={() => onSyncSelectedBikes(selectedBikes)}
        >
          Save My Garage
        </button>
      </div>
    </div>
  );
}
